// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState, useMemo, useEffect } from 'react'
import { ChevronLeft, ChevronRight, Loader2, Check, Save, Sparkles, Mic, ShieldCheck } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { PLAN_OF_CARE_STEPS, TOTAL_STEPS, type WizardField } from './planOfCareSteps'
import AiAssistPanel from './AiAssistPanel'
import { tokenizePHI, detokenize } from './aiClient'
import { fieldAssistPrompt, FIELD_ASSIST_CONTEXT, goalsPrompt } from './aiPrompts'
import DateInput from '../../components/ui/DateInput'

interface Props {
  assessmentId: string
  initialStep: number
  initialPayload: Record<string, any>
  onSaved: () => void
}

/**
 * The 14-step Plan of Care wizard. Payload is a flat object keyed by `${stepKey}.${fieldKey}`,
 * serialized to the assessment's `payload` JSON. Saving persists the payload + current_step,
 * and marks the assessment in_progress (or signed at the final attestation step).
 */
export default function PlanOfCareWizard({ assessmentId, initialStep, initialPayload, onSaved }: Props) {
  const [stepIdx, setStepIdx] = useState(Math.min(Math.max((initialStep || 1) - 1, 0), TOTAL_STEPS - 1))
  const [data, setData] = useState<Record<string, any>>(initialPayload || {})
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [labs, setLabs] = useState<api.CareRxLab[]>([])
  const [pcwDet, setPcwDet] = useState<string | null>(null)

  // Load the assessment's patient + existing baseline labs for the Step 4 subsection (pre-fill).
  useEffect(() => {
    let cancelled = false
    api.getCareRxAssessment(assessmentId).then(r => {
      const pid = r.assessment?.patient_id as string | undefined
      if (pid && !cancelled) {
        api.listCareRxLabs(pid).then(lr => { if (!cancelled) setLabs((lr.items ?? []).map(l => ({ ...l, drawn_at: l.drawn_at ? String(l.drawn_at).slice(0, 10) : l.drawn_at }))) }).catch(() => {})
        api.getCareRxPcwForPatient(pid).then(g => { if (!cancelled) setPcwDet((g.pcw?.therapy_determination as string) || null) }).catch(() => {})
      }
    }).catch(() => {})
    return () => { cancelled = true }
  }, [assessmentId])

  const setLab = (i: number, patch: Partial<api.CareRxLab>) =>
    setLabs(prev => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l))
  const addLab = () => setLabs(prev => [...prev, { analyte: '' }])
  const removeLab = (i: number) => setLabs(prev => prev.filter((_, idx) => idx !== i))

  const step = PLAN_OF_CARE_STEPS[stepIdx]
  const isLast = stepIdx === TOTAL_STEPS - 1
  const progress = useMemo(() => Math.round(((stepIdx + 1) / TOTAL_STEPS) * 100), [stepIdx])

  // Group the step's fields by their subsection (preserving order), mirroring the POT form.
  const groupedFields = useMemo(() => {
    const groups: { sub: string; fields: typeof step.fields }[] = []
    for (const f of step.fields) {
      const sub = f.sub || ''
      let g = groups.find(x => x.sub === sub)
      if (!g) { g = { sub, fields: [] }; groups.push(g) }
      g.fields.push(f)
    }
    return groups
  }, [step])

  const setField = (k: string, v: string) => setData(d => ({ ...d, [k]: v }))

  // AI consent is shared with the AI panel via the same session key. Rather than dead-end
  // the user to "go open the panel", we let them consent inline here and then run the
  // action they were trying to take.
  const [consentPrompt, setConsentPrompt] = useState<null | (() => void)>(null)
  const hasAiConsent = () => sessionStorage.getItem('carerx_ai_consent') === '1'
  const acceptAiConsent = () => {
    sessionStorage.setItem('carerx_ai_consent', '1')
    api.acceptCareRxAiConsent().catch(() => {/* best-effort audit */})
    const act = consentPrompt; setConsentPrompt(null); if (act) act()
  }

  // Per-field AI assist — fills a textarea using POT's exact field-assist prompt. Requires
  // consent (same session key as the panel). PHI tokenized client-side, response detokenized.
  const [aiField, setAiField] = useState<string | null>(null)
  const fieldAssist = async (fullKey: string, fieldId: string) => {
    if (!hasAiConsent()) { setConsentPrompt(() => () => fieldAssist(fullKey, fieldId)); return }
    const ctx = FIELD_ASSIST_CONTEXT[fieldId] || fieldId
    setAiField(fullKey); setMsg(null)
    try {
      const prompt = fieldAssistPrompt(ctx, data)
      const { text: safe, map } = tokenizePHI(prompt, phiValues)
      const r: any = await api.aiAssist([{ role: 'user', content: safe }])
      const restored = detokenize(r.text || '', map)
        .replace(/^(Sure|Certainly|Here|Of course)[^.]*\.\s*/, '').trim()
      if (restored) setField(fullKey, restored)
    } catch (e: any) {
      setMsg(e?.body?.error === 'ai_not_configured'
        ? 'AI not configured yet — your request was PHI-tokenized; nothing was sent externally.'
        : 'AI unavailable — please document manually.')
    } finally { setAiField(null) }
  }

  // AI goals helper — POT's "AI Suggested Goals": returns a JSON array of measurable goals.
  const [goalsBusy, setGoalsBusy] = useState(false)
  const suggestGoals = async () => {
    if (!hasAiConsent()) { setConsentPrompt(() => suggestGoals); return }
    setGoalsBusy(true); setMsg(null)
    const drug = data['infusion_drug_order.drugName'] || ''
    const dx = data['diagnoses.dx1'] || data['diagnoses.primaryDx'] || ''
    try {
      const prompt = goalsPrompt('short-term', drug, dx, '60 days')
      const { text: safe, map } = tokenizePHI(prompt, phiValues)
      const r: any = await api.aiAssist([{ role: 'user', content: safe }])
      const restored = detokenize(r.text || '', map)
      let goals: any[] = []
      try { goals = JSON.parse(restored.replace(/```json|```/g, '').trim()) } catch { /* not JSON */ }
      if (goals.length) {
        const text = goals.map(g => `- ${g.goal_statement} (target: ${g.target_date})`).join('\n')
        setField('goals_frequency_orders.goals', (data['goals_frequency_orders.goals'] ? data['goals_frequency_orders.goals'] + '\n' : '') + text)
      } else { setMsg('AI returned no parseable goals.') }
    } catch (e: any) {
      setMsg(e?.body?.error === 'ai_not_configured' ? 'AI not configured yet (tokenized; nothing sent externally).' : 'Goals suggestion failed.')
    } finally { setGoalsBusy(false) }
  }

  // Voice dictation — Web Speech API; appends transcript to the field.
  const [micField, setMicField] = useState<string | null>(null)
  const dictate = (fullKey: string) => {
    const SR: any = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    if (!SR) { setMsg('Voice dictation is not supported in this browser.'); return }
    const rec = new SR(); rec.lang = 'en-US'; rec.interimResults = false
    setMicField(fullKey)
    rec.onresult = (ev: any) => {
      const t = ev.results[0][0].transcript
      setData(d => ({ ...d, [fullKey]: (d[fullKey] ? d[fullKey] + ' ' : '') + t }))
    }
    rec.onend = () => setMicField(null)
    rec.onerror = () => { setMicField(null); setMsg('Voice dictation error.') }
    rec.start()
  }

  // Context for the AI: only de-identified clinical reference data (drug, indication,
  // diagnosis) — never patient identifiers. The server tokenizer is a second safety net.
  const aiContext = useMemo(() => {
    const drug = data['infusion_drug_order.drugName'] || data['infusion_drug_order.brand'] || ''
    const dx = data['diagnoses.primaryDx'] || data['diagnoses.dx1'] || ''
    const parts = []
    if (drug) parts.push(`Drug/therapy: ${drug}`)
    if (dx) parts.push(`Indication/diagnosis: ${dx}`)
    return parts.length ? `Clinical reference (de-identified) — ${parts.join('; ')}.` : undefined
  }, [data])

  // PHI values entered in the form, redacted client-side before any AI call (first layer).
  const phiValues = useMemo(() => {
    const keys = [
      'patient_certification.ptFirst', 'patient_certification.ptLast', 'patient_certification.ptDOB',
      'patient_certification.ptPhone', 'patient_certification.ptAddr',
      'patient_certification.ptEC',
    ]
    return keys.map(k => data[k]).filter(Boolean) as string[]
  }, [data])

  const persist = async (nextIdx: number, markStatus?: string) => {
    setBusy(true); setMsg(null)
    try {
      await api.updateCareRxAssessment(assessmentId, {
        payload: JSON.stringify(data),
        current_step: nextIdx + 1,
        status: markStatus,
        baseline_labs: labs.filter(l => (l.analyte || '').trim().length > 0),
      })
      setStepIdx(nextIdx)
      onSaved()
    } catch (e: any) { setMsg(friendlyError(e)) } finally { setBusy(false) }
  }

  return (
    <div>
      {consentPrompt && (
        <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(28,24,18,.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'var(--color-surface,#fff)', border: '1px solid var(--color-border,#E8E3DA)', borderRadius: 12, maxWidth: 420, padding: 18 }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>Enable AI assist</div>
            <div style={{ fontSize: 12.5, color: 'var(--color-text-soft,#5b554c)', lineHeight: 1.5, marginBottom: 14 }}>
              The AI assistant helps with drug info, ICD-10 lookup, interaction screening, and clinical
              language. Patient identifiers are removed before any request, and AI output must be verified
              by you before use. Do you consent to AI-assisted documentation?
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={acceptAiConsent} style={{ background: 'var(--color-primary,#1F7A6B)', color: '#fff', border: 'none', borderRadius: 9, padding: '9px 13px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>I consent — enable AI assist</button>
              <button onClick={() => setConsentPrompt(null)} style={{ background: 'var(--color-surface,#fff)', border: '1px solid var(--color-border,#DED8CC)', borderRadius: 9, padding: '9px 13px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {/* progress */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--color-text-mute)', marginBottom: 6 }}>
          <span>Step {stepIdx + 1} of {TOTAL_STEPS} — {step.title}</span><span>{progress}%</span>
        </div>
        <div style={{ height: 6, background: 'var(--color-surface-alt)', borderRadius: 999 }}>
          <div style={{ height: '100%', width: `${progress}%`, background: 'var(--color-primary)', borderRadius: 999, transition: 'width .2s' }} />
        </div>
      </div>

      {/* step nav pills */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 16 }}>
        {PLAN_OF_CARE_STEPS.map((s, i) => (
          <button key={s.key} onClick={() => persist(i)} disabled={busy} title={s.title} style={{
            width: 26, height: 26, borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
            border: '1px solid var(--color-border)',
            background: i === stepIdx ? 'var(--color-primary)' : 'var(--color-surface)',
            color: i === stepIdx ? '#fff' : 'var(--color-text-soft)',
          }}>{i + 1}</button>
        ))}
      </div>

      {pcwDet && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13,
          background: pcwDet === 'approved' ? 'rgba(22,163,74,.1)' : pcwDet === 'denied' ? 'rgba(220,38,38,.1)' : 'rgba(217,119,6,.12)',
          color: pcwDet === 'approved' ? '#15803d' : pcwDet === 'denied' ? '#b91c1c' : '#b45309' }}>
          <ShieldCheck size={15} /> Pharmacist clinical review: therapy <strong style={{ textTransform: 'capitalize' }}>{pcwDet}</strong>
        </div>
      )}

      {/* fields */}
      <h3 style={{ fontSize: 16, margin: '0 0 12px' }}>{step.title}</h3>
      {groupedFields.map(group => (
        <div key={group.sub || '_'} style={{ marginBottom: 18 }}>
          {group.sub && <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: 'var(--color-primary)', margin: '4px 0 10px' }}>{group.sub}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {group.fields.map(f => (
              <div key={f.key} style={{ gridColumn: f.type === 'textarea' ? '1 / -1' : 'auto' }}>
                <FieldInput
                  field={f} stepKey={step.key}
                  value={data[`${step.key}.${f.key}`] ?? ''}
                  onChange={setField}
                  hasAssist={f.type === 'textarea' && !!FIELD_ASSIST_CONTEXT[f.key]}
                  aiBusy={aiField === `${step.key}.${f.key}`}
                  micOn={micField === `${step.key}.${f.key}`}
                  onAi={() => fieldAssist(`${step.key}.${f.key}`, f.key)}
                  onMic={() => dictate(`${step.key}.${f.key}`)}
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      {step.key === 'infusion_drug_order' && (
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: 'var(--color-primary)', margin: '4px 0 6px' }}>Baseline Labs</div>
          <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginBottom: 8 }}>
            Labs entered here sync to the patient's Lab Tracker on save. Expiry is computed server-side from the drawn date + validity days.
          </div>
          {labs.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr .9fr .7fr 1fr .7fr .6fr 30px', gap: 6, fontSize: 11, fontWeight: 600, color: 'var(--color-text-mute)', marginBottom: 4 }}>
              <span>Analyte</span><span>Result</span><span>Unit</span><span>Drawn</span><span>Valid (d)</span><span>Flag</span><span></span>
            </div>
          )}
          {labs.map((l, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.4fr .9fr .7fr 1fr .7fr .6fr 30px', gap: 6, marginBottom: 6, alignItems: 'center' }}>
              <input style={cell} placeholder="e.g. Creatinine" value={l.analyte ?? ''} onChange={e => setLab(i, { analyte: e.target.value })} />
              <input style={cell} placeholder="value" value={l.value_text ?? ''} onChange={e => setLab(i, { value_text: e.target.value })} />
              <input style={cell} placeholder="mg/dL" value={l.unit ?? ''} onChange={e => setLab(i, { unit: e.target.value })} />
              <DateInput label="Lab drawn on" style={cell} value={l.drawn_at ?? ''} onChange={v => setLab(i, { drawn_at: v })} />
              <input style={cell} type="number" placeholder="30" value={l.validity_days ?? ''} onChange={e => setLab(i, { validity_days: e.target.value === '' ? null : Number(e.target.value) })} />
              <select style={cell} value={l.abnormal_flag ?? ''} onChange={e => setLab(i, { abnormal_flag: e.target.value || null })}>
                <option value="">—</option><option value="H">H</option><option value="L">L</option><option value="A">A</option>
              </select>
              <button type="button" title="Remove lab" onClick={() => removeLab(i)} style={{ ...ghost, padding: '4px 0', justifyContent: 'center' }}>×</button>
            </div>
          ))}
          <button type="button" style={{ ...ghost, marginTop: 4 }} onClick={addLab}>+ Add lab</button>
        </div>
      )}

      {step.key === 'goals_frequency_orders' && (
        <button type="button" style={{ ...ghost, marginTop: 4 }} disabled={goalsBusy} onClick={suggestGoals}>
          {goalsBusy ? <Loader2 className="spin" size={14} /> : <Sparkles size={14} />} AI suggested goals
        </button>
      )}

      {msg && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 10 }}>{msg}</div>}

      {/* AI clinical assist — sends only de-identified clinical reference data (drug, dx). */}
      <AiAssistPanel context={aiContext} phiValues={phiValues} />

      {/* controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18 }}>
        <button style={ghost} disabled={busy || stepIdx === 0} onClick={() => persist(stepIdx - 1)}>
          <ChevronLeft size={15} /> Back
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={ghost} disabled={busy} onClick={() => persist(stepIdx, 'in_progress')}>
            {busy ? <Loader2 className="spin" size={14} /> : <Save size={14} />} Save
          </button>
          {isLast ? (
            <button style={primary} disabled={busy} onClick={() => persist(stepIdx, 'pending_signature')}>
              <Check size={15} /> Complete & ready for signature
            </button>
          ) : (
            <button style={primary} disabled={busy} onClick={() => persist(stepIdx + 1, 'in_progress')}>
              Next <ChevronRight size={15} />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function FieldInput({ field, stepKey, value, onChange, hasAssist, aiBusy, micOn, onAi, onMic }: {
  field: WizardField; stepKey: string; value: string; onChange: (k: string, v: string) => void
  hasAssist?: boolean; aiBusy?: boolean; micOn?: boolean; onAi?: () => void; onMic?: () => void
}) {
  const k = `${stepKey}.${field.key}`
  const common = { value, onChange: (e: any) => onChange(k, e.target.value), style: input }
  const htmlType = field.type === 'time' ? 'time'
    : field.type === 'number' ? 'number' : field.type === 'tel' ? 'tel' : 'text'
  return (
    <label style={{ display: 'block' }}>
      <span style={{ ...lbl, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {field.label}
        {hasAssist && (
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <button type="button" title="AI assist" onClick={onAi} disabled={aiBusy} style={faBtn}>
              {aiBusy ? <Loader2 className="spin" size={13} /> : <Sparkles size={13} />}
            </button>
            <button type="button" title="Dictate" onClick={onMic} style={{ ...faBtn, color: micOn ? '#dc2626' : 'var(--color-primary)' }}>
              <Mic size={13} />
            </button>
          </span>
        )}
      </span>
      {field.type === 'textarea' ? (
        <textarea {...common} rows={3} style={{ ...input, resize: 'vertical' }} />
      ) : field.type === 'select' ? (
        <select {...common}>{(field.options || []).map((o, i) => <option key={i} value={o}>{o || '—'}</option>)}</select>
      ) : field.type === 'date' ? (
        // Typed or picked, saved once complete (ASP 2026-10-15: the native date field would not take typing).
        <DateInput label={field.label} value={value} onChange={v => onChange(k, v ?? '')} style={{ ...input, padding: '4px 6px 4px 9px' }} />
      ) : field.type === 'checkbox' ? (
        <input type="checkbox" checked={value === 'true'} onChange={e => onChange(k, e.target.checked ? 'true' : 'false')} style={{ width: 18, height: 18 }} />
      ) : (
        <input {...common} type={htmlType} />
      )}
    </label>
  )
}

const lbl: React.CSSProperties = { display: 'block', fontSize: 12, color: 'var(--color-text-mute)', marginBottom: 4 }
const input: React.CSSProperties = { width: '100%', padding: '7px 9px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, background: 'var(--color-surface)', color: 'var(--color-text)', boxSizing: 'border-box' }
const primary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const ghost: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 13, cursor: 'pointer' }
const faBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 5, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-primary)', cursor: 'pointer', padding: 0 }
const cell: React.CSSProperties = { width: '100%', padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 12, background: 'var(--color-surface)', color: 'var(--color-text)', boxSizing: 'border-box' }
